import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Loader2,
  Save,
  Plus,
  Trash2,
  ArrowLeft,
  Layers,
  Eye,
  SlidersHorizontal,
  ChevronRight,
  GripVertical,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';
import { clsx } from 'clsx';
import { useEditorStore } from '@/stores/editor';
import { CAPTION_STYLES } from '@/core/composer/CaptionRenderer';
import type { Emotion, CameraPreset, CaptionStyle } from '@/types/scene';

type Tab = 'scenes' | 'preview' | 'edit';

export function Editor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('preview');
  const [saving, setSaving] = useState(false);

  const {
    projectId,
    title,
    sceneJson,
    selectedSceneId,
    isDirty,
    isLoading,
    loadProject,
    newProject,
    setTitle,
    addScene,
    updateScene,
    removeScene,
    reorderScenes,
    selectScene,
    save,
  } = useEditorStore();

  useEffect(() => {
    if (id === 'new') {
      newProject();
    } else if (id) {
      void loadProject(id).catch(() => navigate('/'));
    }
  }, [id, loadProject, newProject, navigate]);

  async function handleSave() {
    if (!isDirty || saving) return;
    setSaving(true);
    try {
      const saved = await save();
      if (saved && !projectId) navigate(`/editor/${saved.id}`, { replace: true });
    } finally {
      setSaving(false);
    }
  }

  if (isLoading || !sceneJson) {
    return (
      <div className="flex h-[60vh] items-center justify-center gap-2 text-slate-400">
        <Loader2 className="h-4 w-4 animate-spin" /> 読み込み中…
      </div>
    );
  }

  const selected = sceneJson.scenes.find((s) => s.id === selectedSceneId);

  return (
    <div className="flex h-full flex-col">
      {/* Editor header (replaces app nav in editor mode) */}
      <div className="sticky top-0 z-20 flex items-center gap-2 border-b border-slate-800 bg-slate-950/95 px-3 py-2 backdrop-blur">
        <Link
          to="/"
          className="flex h-10 w-10 items-center justify-center rounded-lg text-slate-300 active:bg-slate-800"
          aria-label="戻る"
        >
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="flex-1 bg-transparent text-base font-medium text-slate-100 placeholder:text-slate-500 focus:outline-none"
          placeholder="プロジェクト名"
        />
        <button
          onClick={handleSave}
          disabled={!isDirty || saving}
          className={clsx(
            'flex h-10 items-center gap-1 rounded-lg px-3 text-sm font-medium min-w-[60px] justify-center',
            isDirty
              ? 'bg-brand-500 text-white active:bg-brand-700'
              : 'bg-slate-800 text-slate-400',
          )}
        >
          {saving ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <>
              <Save className="h-4 w-4" />
              保存
            </>
          )}
        </button>
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-hidden">
        {tab === 'scenes' && (
          <ScenesTab
            scenes={sceneJson.scenes}
            selectedId={selectedSceneId}
            onSelect={(id) => {
              selectScene(id);
              setTab('edit');
            }}
            onAdd={() => {
              addScene();
              setTab('edit');
            }}
            onRemove={removeScene}
            onReorder={reorderScenes}
          />
        )}
        {tab === 'preview' && <PreviewTab selected={selected ?? null} />}
        {tab === 'edit' && selected && (
          <EditTab
            selected={selected}
            onUpdate={(patch) => updateScene(selected.id, patch)}
            onRemove={() => {
              removeScene(selected.id);
              setTab('scenes');
            }}
          />
        )}
        {tab === 'edit' && !selected && (
          <div className="p-6 text-center text-sm text-slate-500">
            まずシーンを選択してください。
            <div className="mt-4">
              <button onClick={() => setTab('scenes')} className="btn-secondary">
                シーン一覧へ
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Editor bottom tabs */}
      <nav
        className="border-t border-slate-800 bg-slate-950/95 backdrop-blur"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        <div className="flex items-stretch justify-around">
          <TabButton
            label="シーン"
            icon={Layers}
            active={tab === 'scenes'}
            onClick={() => setTab('scenes')}
            badge={sceneJson.scenes.length}
          />
          <TabButton
            label="プレビュー"
            icon={Eye}
            active={tab === 'preview'}
            onClick={() => setTab('preview')}
          />
          <TabButton
            label="編集"
            icon={SlidersHorizontal}
            active={tab === 'edit'}
            onClick={() => setTab('edit')}
            disabled={!selected}
          />
        </div>
      </nav>
    </div>
  );
}

function TabButton({
  label,
  icon: Icon,
  active,
  onClick,
  disabled,
  badge,
}: {
  label: string;
  icon: typeof Layers;
  active: boolean;
  onClick: () => void;
  disabled?: boolean;
  badge?: number;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={clsx(
        'relative flex flex-1 flex-col items-center justify-center gap-0.5 py-2 text-[11px] min-h-[56px]',
        active ? 'text-brand-400' : 'text-slate-400',
        disabled && 'opacity-40',
      )}
    >
      <Icon className="h-5 w-5" />
      <span>{label}</span>
      {badge !== undefined && badge > 0 && (
        <span className="absolute top-1 right-[calc(50%-20px)] flex h-4 min-w-4 items-center justify-center rounded-full bg-slate-700 px-1 text-[9px]">
          {badge}
        </span>
      )}
    </button>
  );
}

// -------------------- Scenes Tab --------------------
function ScenesTab({
  scenes,
  selectedId,
  onSelect,
  onAdd,
  onRemove,
  onReorder,
}: {
  scenes: Array<{ id: string; t_start: number; t_end: number; narration: string }>;
  selectedId: string | null;
  onSelect: (id: string) => void;
  onAdd: () => void;
  onRemove: (id: string) => void;
  onReorder: (from: number, to: number) => void;
}) {
  return (
    <div className="h-full overflow-auto px-3 py-4">
      <div className="space-y-2">
        {scenes.map((s, idx) => (
          <div
            key={s.id}
            className={clsx(
              'flex items-stretch rounded-xl border bg-slate-900/60',
              s.id === selectedId ? 'border-brand-500/60' : 'border-slate-800',
            )}
          >
            <button
              onClick={() => onSelect(s.id)}
              className="flex-1 px-3 py-3 text-left min-h-[72px]"
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-slate-500">シーン {idx + 1}</span>
                <span className="text-xs text-slate-500">
                  {s.t_start.toFixed(1)}〜{s.t_end.toFixed(1)}s
                </span>
              </div>
              <div className="text-sm text-slate-200 line-clamp-2">
                {s.narration || (
                  <span className="text-slate-500">(ナレーション未入力)</span>
                )}
              </div>
            </button>
            <div className="flex flex-col border-l border-slate-800">
              <button
                onClick={() => onReorder(idx, idx - 1)}
                disabled={idx === 0}
                className="flex h-9 w-10 items-center justify-center text-slate-400 disabled:opacity-30 active:bg-slate-800"
                aria-label="上へ"
              >
                <ArrowUp className="h-4 w-4" />
              </button>
              <button
                onClick={() => onReorder(idx, idx + 1)}
                disabled={idx === scenes.length - 1}
                className="flex h-9 w-10 items-center justify-center border-t border-slate-800 text-slate-400 disabled:opacity-30 active:bg-slate-800"
                aria-label="下へ"
              >
                <ArrowDown className="h-4 w-4" />
              </button>
              <button
                onClick={() => onRemove(s.id)}
                className="flex h-9 w-10 items-center justify-center border-t border-slate-800 text-red-400 active:bg-red-500/20"
                aria-label="削除"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
      <button onClick={onAdd} className="btn-secondary w-full mt-4">
        <Plus className="h-4 w-4" />
        シーンを追加
      </button>
    </div>
  );
}

// -------------------- Preview Tab --------------------
function PreviewTab({
  selected,
}: {
  selected: {
    background: { type: string; value: string };
    caption?: { text: string; style: CaptionStyle; position: string } | null;
  } | null;
}) {
  return (
    <div className="flex h-full items-center justify-center p-4 bg-slate-950">
      <div
        className="relative w-full max-w-[320px] rounded-xl shadow-2xl overflow-hidden flex items-center justify-center"
        style={{
          aspectRatio: '9 / 16',
          background:
            selected?.background.type === 'gradient'
              ? selected.background.value
              : selected?.background.value ?? '#0f172a',
        }}
      >
        <div className="absolute inset-0 flex items-center justify-center text-white/70 text-xs px-6 text-center">
          [3D プレビュー - 動画生成は今後のリリース]
        </div>
        {selected?.caption && (
          <div
            className="absolute left-0 right-0 text-center font-bold pointer-events-none px-2"
            style={{
              top:
                selected.caption.position === 'top'
                  ? '18%'
                  : selected.caption.position === 'center'
                    ? '45%'
                    : '72%',
              color: CAPTION_STYLES[selected.caption.style].fillColor,
              WebkitTextStroke: `${CAPTION_STYLES[selected.caption.style].strokeWidth / 5}px ${CAPTION_STYLES[selected.caption.style].strokeColor}`,
              fontSize: `${CAPTION_STYLES[selected.caption.style].size / 5}px`,
              fontFamily: CAPTION_STYLES[selected.caption.style].font,
              lineHeight: 1.1,
            }}
          >
            {selected.caption.text}
          </div>
        )}
      </div>
    </div>
  );
}

// -------------------- Edit Tab --------------------
function EditTab({
  selected,
  onUpdate,
  onRemove,
}: {
  selected: any;
  onUpdate: (patch: any) => void;
  onRemove: () => void;
}) {
  return (
    <div className="h-full overflow-auto px-4 py-4 space-y-4">
      <div>
        <label className="label">ナレーション</label>
        <textarea
          value={selected.narration}
          onChange={(e) => onUpdate({ narration: e.target.value })}
          className="input min-h-[120px] resize-none"
          placeholder="このシーンで読み上げるテキスト"
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="label">開始 (秒)</label>
          <input
            type="number"
            step="0.1"
            value={selected.t_start}
            onChange={(e) => onUpdate({ t_start: parseFloat(e.target.value) || 0 })}
            className="input"
            inputMode="decimal"
          />
        </div>
        <div>
          <label className="label">終了 (秒)</label>
          <input
            type="number"
            step="0.1"
            value={selected.t_end}
            onChange={(e) => onUpdate({ t_end: parseFloat(e.target.value) || 0 })}
            className="input"
            inputMode="decimal"
          />
        </div>
      </div>
      <div>
        <label className="label">感情</label>
        <SegmentedControl
          value={selected.emotion}
          onChange={(v) => onUpdate({ emotion: v as Emotion })}
          options={[
            { value: 'neutral', label: 'ふつう' },
            { value: 'happy', label: '喜' },
            { value: 'surprised', label: '驚' },
            { value: 'serious', label: '真剣' },
            { value: 'sad', label: '悲' },
          ]}
        />
      </div>
      <div>
        <label className="label">カメラ</label>
        <SegmentedControl
          value={selected.camera.preset}
          onChange={(v) =>
            onUpdate({ camera: { ...selected.camera, preset: v as CameraPreset } })
          }
          options={[
            { value: 'bust', label: 'バスト' },
            { value: 'close', label: '寄り' },
            { value: 'wide', label: '引き' },
          ]}
        />
      </div>
      <div>
        <label className="label">キャプション(最大12文字)</label>
        <input
          value={selected.caption?.text ?? ''}
          onChange={(e) =>
            onUpdate({
              caption: e.target.value
                ? {
                    text: e.target.value,
                    style: selected.caption?.style ?? 'yellow_impact',
                    position: selected.caption?.position ?? 'top',
                  }
                : null,
            })
          }
          className="input"
          placeholder="(空白で非表示)"
          maxLength={12}
        />
        {selected.caption && (
          <>
            <div className="mt-3">
              <label className="label">スタイル</label>
              <SegmentedControl
                value={selected.caption.style}
                onChange={(v) =>
                  onUpdate({
                    caption: { ...selected.caption!, style: v as CaptionStyle },
                  })
                }
                options={[
                  { value: 'yellow_impact', label: '黄' },
                  { value: 'white_clean', label: '白' },
                  { value: 'red_alert', label: '赤' },
                  { value: 'black_minimal', label: '黒' },
                ]}
              />
            </div>
            <div className="mt-3">
              <label className="label">位置</label>
              <SegmentedControl
                value={selected.caption.position}
                onChange={(v) =>
                  onUpdate({
                    caption: { ...selected.caption!, position: v as 'top' | 'center' | 'bottom' },
                  })
                }
                options={[
                  { value: 'top', label: '上' },
                  { value: 'center', label: '中央' },
                  { value: 'bottom', label: '下' },
                ]}
              />
            </div>
          </>
        )}
      </div>
      <button onClick={onRemove} className="btn-ghost w-full text-red-400">
        <Trash2 className="h-4 w-4" />
        このシーンを削除
      </button>
      <div className="h-4" />
    </div>
  );
}

function SegmentedControl<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T;
  onChange: (v: T) => void;
  options: Array<{ value: T; label: string }>;
}) {
  return (
    <div className="flex rounded-lg bg-slate-900 border border-slate-700 overflow-hidden">
      {options.map((opt) => (
        <button
          key={opt.value}
          onClick={() => onChange(opt.value)}
          className={clsx(
            'flex-1 px-2 py-3 text-sm min-h-[44px] transition',
            value === opt.value
              ? 'bg-brand-500 text-white'
              : 'text-slate-300 active:bg-slate-800',
          )}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// Dummy-use to keep tree-shaking from dropping icons referenced only in comments
const _unused = { GripVertical, ChevronRight };
void _unused;
